<template>
    <main class="mx-auto max-w-screen-xl px-4 mt-6 lg:mt-12">
        <div class="flex flex-col">
            <div class="max-w-lg mx-auto" v-if="account.logo">
                <img :alt="account.title" style="max-height: 300px;" :src="account.logo" />
            </div>

            <div class="mt-4 lg:mt-8 lg:grid lg:grid-cols-2 lg:divide-x lg:divide-gray-200">
                <div class="p-6 text-left lg:text-right">
                    <h2 class="text-2xl tracking-tight leading-10 text-gray-900 sm:text-4xl">
                        {{ account.title }}
                    </h2>
                    <h3 class="text-base text-gray-500 sm:text-xl lg:text-lg xl:text-xl" v-if="account.slogan">
                        <span class="whitespace-pre-wrap">{{ account.slogan }}</span>
                    </h3>
                    <p class="mt-3 text-base text-gray-500 sm:mt-5 sm:text-xl lg:text-lg xl:text-xl" v-if="account.description">
                        <span class="whitespace-pre-wrap">{{ account.description }}</span>
                    </p>
                </div>
                <div class="p-6 pt-8 flex text-left">
                    <dl class="space-y-2 text-base leading-6 text-gray-500">
                        <div class="flex" v-if="account.address">
                            <dt class="sr-only">
                                Adresse
                            </dt>
                            <dd>
                                <p>
                                    {{ account.address }}
                                </p>
                                <p>
                                    {{ account.city }}, {{ account.province }}&nbsp;&nbsp;{{ account.postal_code }}
                                </p>
                            </dd>
                        </div>
                        <div class="flex" v-if="account.phone">
                            <dt class="sr-only">
                                Téléphone
                            </dt>
                            <dd class="inline-flex">
                                <!-- Heroicon name: phone -->
                                <svg
                                    class="flex-shrink-0 h-6 w-6 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewbox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                    />
                                </svg>
                                <span class="ml-3">
                                    <a :href="`tel:${account.phone.replace('-', '').replace(' ', '').replace('(', '').replace(')', '')}`">
                                        {{ account.phone }}
                                    </a>
                                </span>
                            </dd>
                        </div>
                        <div class="flex" v-if="account.email">
                            <dt class="sr-only">
                                Courriel
                            </dt>
                            <dd class="inline-flex">
                                <!-- Heroicon name: mail -->
                                <svg
                                    class="flex-shrink-0 h-6 w-6 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewbox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                    />
                                </svg>
                                <span class="ml-3">
                                    <a :href="`mailto:${account.email}`">
                                        {{ account.email }}
                                    </a>
                                </span>
                            </dd>
                        </div>
                        <div class="flex" v-if="account.facebook">
                            <dt class="sr-only">
                                Liens sociaux
                            </dt>
                            <dd>
                                <a class="inline-flex" :href="account.facebook" target="_blank">
                                    <svg
                                        aria-hidden="true"
                                        class="flex-shrink-0 h-6 w-6 text-gray-400"
                                        fill="currentColor"
                                        viewbox="0 0 24 24"
                                    >
                                        <path clip-rule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" fill-rule="evenodd" />
                                    </svg>
                                    <span class="ml-3">
                                        Visitez nous sur Facebook!
                                    </span>
                                </a>
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>

            <div class="bg-white py-8 sm:py-12" v-if="subscribed && mailchimp && mailchimp.pivot.text && mailchimp.pivot.meta.api_key && mailchimp.pivot.meta.audience_id">
                <div class="relative sm:py-16">
                    <div class="mx-auto max-w-md px-4 sm:max-w-3xl sm:px-6 lg:max-w-7xl lg:px-8">
                        <div class="relative overflow-hidden rounded-2xl bg-gray-600 px-6 py-10 shadow-xl sm:px-12 sm:py-20">
                            <div aria-hidden="true" class="absolute inset-0 -mt-72 sm:-mt-32 md:mt-0">
                                <svg
                                    class="absolute inset-0 h-full w-full"
                                    preserveAspectRatio="xMidYMid slice"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 1463 360"
                                >
                                    <path class="text-gray-500 text-opacity-40" fill="currentColor" d="M-82.673 72l1761.849 472.086-134.327 501.315-1761.85-472.086z" />
                                    <path class="text-gray-700 text-opacity-40" fill="currentColor" d="M-217.088 544.086L1544.761 72l134.327 501.316-1761.849 472.086z" />
                                </svg>
                            </div>
                            <div class="relative">
                                <div class="sm:text-center">
                                    <h2 class="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                                        {{ mailchimp.pivot.text }}
                                    </h2>
                                </div>
                                <form class="mt-12 sm:mx-auto sm:flex sm:max-w-lg" @submit.prevent="signup">
                                    <div class="min-w-0 flex-1">
                                        <label for="cta-email" class="sr-only">Adresse courriel</label>
                                        <input
                                            id="cta-email"
                                            type="email"
                                            class="block w-full rounded-md border border-transparent px-5 py-3 text-base text-gray-900 placeholder-gray-500 shadow-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-600"
                                            placeholder="Entrez votre adresse courriel"
                                            v-model="email"
                                        />
                                    </div>
                                    <div class="mt-4 sm:mt-0 sm:ml-3">
                                        <button type="submit" class="block w-full rounded-md border border-transparent bg-gray-500 px-5 py-3 text-base font-medium text-white shadow hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-600 sm:px-10">
                                            S'inscrire
                                        </button>
                                    </div>
                                </form>
                                <div class="mt-2 sm:mx-auto sm:max-w-lg rounded-md bg-green-50 p-4" v-if="isSignup">
                                    <div class="flex">
                                        <div class="flex-shrink-0">
                                            <!-- Heroicon name: mini/check-circle -->
                                            <svg
                                                class="h-5 w-5 text-green-400"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                                aria-hidden="true"
                                            >
                                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
                                            </svg>
                                        </div>
                                        <div class="ml-3">
                                            <p class="text-sm font-medium text-green-800">
                                                {{ $t('gestion.integration.mailchimp.success') }}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </main>
</template>

<script>
    import Preview from '@/layouts/Preview.vue';

    export default {
        layout: Preview,

        props: {
            account: {
                type: Object,
                default: () => {},
            },
            subscribed: {
                type: Boolean,
                default: false,
            },
            mailchimp: {
                type: Object,
                default: () => {},
            },
        },

        data: () => ({
            isSignup: false,
            email: '',
        }),

        methods: {
            signup() {
                this.isSignup = true;
                this.email = '';
            },
        },
    };
</script>
